import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { Image } from 'expo-image';
import { Tabs, usePathname, useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { HapticTab } from '@/components/haptic-tab';
import { Fonts } from '@/constants/theme';
import { fetchTeacherQuestionSets, type TeacherQuestionSetListItem } from '@/lib/question-sets';
import { useAuth } from '@/providers/auth-provider';
import logoSource from '../../assets/images/mcqgen-logo-trimmed.png';

type NavItem = {
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  route: string;
};

const desktopNavItems: NavItem[] = [
  { label: 'Home', icon: 'home', route: '/teacher' },
  { label: 'Sets', icon: 'auto-stories', route: '/teacher/sets' },
  { label: 'Scan', icon: 'qr-code-scanner', route: '/teacher/scan-desktop' },
  { label: 'Results', icon: 'analytics', route: '/teacher/results' },
  { label: 'Assign', icon: 'assignment', route: '/teacher/assignments' },
];

const navSections = [
  {
    label: 'Platform',
    items: desktopNavItems.slice(0, 1),
  },
  {
    label: 'Teaching',
    items: desktopNavItems.slice(1),
  },
];

function getShortName(value: string | null | undefined) {
  const trimmed = value?.trim();

  if (!trimmed) {
    return 'Teacher';
  }

  return trimmed.split(' ')[0] ?? 'Teacher';
}

function getProfilePhotoUrl(metadata: Record<string, unknown> | undefined) {
  const candidates = [
    metadata?.avatar_url,
    metadata?.picture,
    metadata?.photo_url,
    metadata?.profile_photo_url,
  ];

  for (const value of candidates) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

export default function TeacherLayout() {
  const { profile, session } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;

  const [modalVisible, setModalVisible] = useState(false);
  const [sets, setSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const activeTitle = useMemo(() => {
    const matched = desktopNavItems.find((item) =>
      item.route === '/teacher'
        ? pathname === '/teacher' || pathname === '/teacher/index'
        : pathname === item.route || pathname.startsWith(`${item.route}/`)
    );

    if (matched) return matched.label;
    if (pathname.includes('/teacher/account')) return 'Account';
    return 'Teacher';
  }, [pathname]);

  const isHome = pathname === '/teacher' || pathname === '/teacher/index';
  const teacherName = getShortName(profile?.full_name);
  const profilePhotoUrl = getProfilePhotoUrl(
    session?.user.user_metadata as Record<string, unknown> | undefined
  );

  const goTo = (route: string) => {
    router.push(route as never);
  };

  const isActiveRoute = (route: string) =>
    route === '/teacher'
      ? pathname === '/teacher' || pathname === '/teacher/index'
      : pathname === route || pathname.startsWith(`${route}/`);

  const handleOpenScanModal = async () => {
    setModalVisible(true);
    if (profile?.id) {
      setLoading(true);
      try {
        const data = await fetchTeacherQuestionSets(profile.id);
        setSets(data);
      } catch (err) {
        console.error('Error fetching sets for scan:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectSet = (setId: string) => {
    setModalVisible(false);
    router.push({
      pathname: '/teacher/scan',
      params: { setId },
    });
  };

  const tabBarStyle = isDesktop ? styles.hiddenTabBar : styles.tabBar;

  const tabs = (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarButton: HapticTab,
        tabBarActiveTintColor: '#163828',
        tabBarInactiveTintColor: '#7F8B80',
        tabBarShowLabel: true,
        tabBarItemStyle: isDesktop ? styles.desktopTabItem : undefined,
        tabBarLabelStyle: isDesktop ? styles.desktopTabLabel : styles.tabBarLabel,
        tabBarStyle,
      }}>
      <Tabs.Screen
        name="index"
        options={{
          title: 'Home',
          tabBarIcon: ({ color, focused }) => (
            <MaterialIcons name="home" size={24} color={focused ? '#163828' : color} />
          ),
        }}
      />
      <Tabs.Screen
        name="sets"
        options={{
          title: 'Sets',
          tabBarIcon: ({ color, focused }) => (
            <MaterialIcons name="auto-stories" size={24} color={focused ? '#163828' : color} />
          ),
        }}
      />
      <Tabs.Screen
        name="scan"
        options={{
          title: 'Scan',
          tabBarIcon: () => (
            <View style={styles.scanIconContainer}>
              <MaterialIcons name="qr-code-scanner" size={28} color="#F7F4EE" />
            </View>
          ),
          tabBarLabel: () => null,
        }}
        listeners={{
          tabPress: (e) => {
            e.preventDefault();
            handleOpenScanModal();
          },
        }}
      />
      <Tabs.Screen
        name="scan-desktop"
        options={{
          href: null,
        }}
      />
      <Tabs.Screen
        name="results"
        options={{
          title: 'Results',
          tabBarIcon: ({ color, focused }) => (
            <MaterialIcons name="analytics" size={24} color={focused ? '#163828' : color} />
          ),
        }}
      />
      <Tabs.Screen
        name="assignments"
        options={{
          title: 'Assign',
          tabBarIcon: ({ color, focused }) => (
            <MaterialIcons name="assignment" size={24} color={focused ? '#163828' : color} />
          ),
        }}
      />
      <Tabs.Screen
        name="account"
        options={{
          href: null,
        }}
      />
      <Tabs.Screen
        name="class-students"
        options={{
          href: null,
        }}
      />
    </Tabs>
  );

  return (
    <>
      {isDesktop ? (
        <View style={styles.desktopShell}>
          <View style={styles.sidebar}>
            <View style={styles.sidebarTop}>
              <View style={styles.sidebarHeader}>
                <View style={styles.brandRow}>
                  <View style={styles.brandMark}>
                    <Image contentFit="contain" source={logoSource} style={styles.brandLogo} />
                  </View>
                  <View style={styles.brandText}>
                    <Text style={styles.brandName}>MCQGen</Text>
                    <Text style={styles.brandSub}>Teacher workspace</Text>
                  </View>
                </View>
              </View>

              {navSections.map((section) => (
                <View key={section.label} style={styles.sidebarSection}>
                  <Text style={styles.sidebarLabel}>{section.label}</Text>
                  <View style={styles.navList}>
                    {section.items.map((item) => {
                      const isActive = isActiveRoute(item.route);

                      return (
                        <Pressable
                          key={item.label}
                          onPress={() => goTo(item.route)}
                          style={({ pressed }) => [
                            styles.sidebarItem,
                            isActive && styles.sidebarItemActive,
                            pressed && styles.sidebarItemPressed,
                          ]}>
                          <View
                            style={[
                              styles.sidebarItemIndicator,
                              isActive && styles.sidebarItemIndicatorActive,
                            ]}
                          />
                          <View
                            style={[
                              styles.sidebarIconWrap,
                              isActive && styles.sidebarIconWrapActive,
                            ]}>
                            <MaterialIcons
                              color={isActive ? '#17412E' : '#6E7E72'}
                              name={item.icon}
                              size={18}
                            />
                          </View>
                          <Text
                            style={[
                              styles.sidebarLabelText,
                              isActive && styles.sidebarLabelActive,
                            ]}>
                            {item.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ))}
            </View>

            <Pressable
              onPress={() => goTo('/teacher/account')}
              style={({ pressed }) => [
                styles.sidebarFooter,
                isActiveRoute('/teacher/account') && styles.sidebarFooterActive,
                pressed && styles.sidebarItemPressed,
              ]}>
              {profilePhotoUrl ? (
                <Image
                  contentFit="cover"
                  source={{ uri: profilePhotoUrl }}
                  style={styles.sidebarFooterPhoto}
                />
              ) : (
                <View style={styles.sidebarFooterAvatar}>
                  <Text style={styles.sidebarFooterAvatarText}>
                    {teacherName.slice(0, 1).toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={styles.sidebarFooterCopy}>
                <Text style={styles.sidebarFooterTitle} numberOfLines={1}>
                  {profile?.full_name?.trim() || 'Teacher'}
                </Text>
                <Text style={styles.sidebarFooterMeta} numberOfLines={1}>
                  {profile?.email || (isHome ? 'Teacher workspace' : activeTitle)}
                </Text>
              </View>
              <MaterialIcons color="#6B7280" name="more-horiz" size={18} />
            </Pressable>
          </View>

          <View style={styles.desktopContent}>{tabs}</View>
        </View>
      ) : (
        tabs
      )}

      <Modal visible={modalVisible} transparent animationType="slide" onRequestClose={() => setModalVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHandle} />
            <Text style={styles.modalTitle}>Select MCQ Set</Text>
            <Text style={styles.modalSub}>Pick a set to begin scanning OMR sheets</Text>

            {loading ? (
              <ActivityIndicator color="#1B5E20" size="large" style={{ marginVertical: 40 }} />
            ) : sets.length === 0 ? (
              <View style={styles.emptySets}>
                <MaterialIcons name="info-outline" size={40} color="#CCC" />
                <Text style={styles.emptySetsText}>No MCQ sets available. Create one first!</Text>
                <Pressable style={styles.closeBtn} onPress={() => setModalVisible(false)}>
                  <Text style={styles.closeBtnText}>CLOSE</Text>
                </Pressable>
              </View>
            ) : (
              <ScrollView style={styles.setsScroll} showsVerticalScrollIndicator={false}>
                {sets.map((set) => (
                  <Pressable key={set.id} style={styles.setItem} onPress={() => handleSelectSet(set.id)}>
                    <View style={styles.setItemIcon}>
                      <MaterialIcons name="article" size={20} color="#1B5E20" />
                    </View>
                    <View style={styles.setItemInfo}>
                      <Text style={styles.setItemTitle}>{set.title}</Text>
                      <Text style={styles.setItemDept}>
                        {set.class_label} · {set.subject_label}
                      </Text>
                    </View>
                    <MaterialIcons name="chevron-right" size={20} color="#B5C2B6" />
                  </Pressable>
                ))}
              </ScrollView>
            )}

            {!loading && sets.length > 0 && (
              <Pressable style={styles.cancelBtn} onPress={() => setModalVisible(false)}>
                <Text style={styles.cancelBtnT}>CANCEL</Text>
              </Pressable>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  desktopShell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#EDF3EC',
  },
  sidebar: {
    width: 276,
    backgroundColor: '#FAFAFA',
    paddingHorizontal: 16,
    paddingVertical: 18,
    justifyContent: 'space-between',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
  },
  sidebarTop: {
    gap: 20,
  },
  sidebarHeader: {
    gap: 10,
    paddingHorizontal: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  brandMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  brandLogo: {
    width: 28,
    height: 28,
  },
  brandText: {
    gap: 2,
  },
  brandName: {
    color: '#173A2A',
    fontFamily: Fonts.rounded,
    fontSize: 16,
    fontWeight: '800',
  },
  brandSub: {
    color: '#6B7280',
    fontSize: 12,
    fontWeight: '600',
  },
  sidebarSection: {
    gap: 8,
  },
  sidebarLabel: {
    color: '#9CA3AF',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 8,
  },
  navList: {
    gap: 4,
  },
  sidebarItem: {
    minHeight: 38,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  sidebarItemActive: {
    backgroundColor: '#F3F4F6',
  },
  sidebarItemPressed: {
    opacity: 0.78,
  },
  sidebarItemIndicator: {
    width: 3,
    height: 16,
    borderRadius: 999,
    backgroundColor: 'transparent',
  },
  sidebarItemIndicatorActive: {
    backgroundColor: '#1F5A37',
  },
  sidebarIconWrap: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  sidebarIconWrapActive: {
    backgroundColor: 'transparent',
  },
  sidebarLabelText: {
    flex: 1,
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
  },
  sidebarLabelActive: {
    color: '#173A2A',
    fontWeight: '800',
  },
  sidebarFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 8,
    minHeight: 52,
    paddingHorizontal: 8,
    paddingTop: 14,
    paddingBottom: 6,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    borderRadius: 10,
  },
  sidebarFooterActive: {
    backgroundColor: '#F3F4F6',
  },
  sidebarFooterAvatar: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1F5A37',
  },
  sidebarFooterAvatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  sidebarFooterPhoto: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: '#E5E7EB',
  },
  sidebarFooterCopy: {
    flex: 1,
    gap: 2,
  },
  sidebarFooterTitle: {
    color: '#173A2A',
    fontSize: 13,
    fontWeight: '700',
  },
  sidebarFooterMeta: {
    color: '#6B7280',
    fontSize: 12,
  },
  desktopContent: {
    flex: 1,
    minWidth: 0,
  },
  hiddenTabBar: {
    display: 'none',
  },
  tabBar: {
    backgroundColor: 'white',
    height: 80,
    paddingBottom: 20,
    paddingTop: 10,
    borderTopWidth: 0,
    elevation: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
  },
  desktopTabItem: {
    paddingTop: 6,
  },
  tabBarLabel: {
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
  },
  desktopTabLabel: {
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
  },
  scanIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#1B5E20',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -30,
    elevation: 4,
    shadowColor: '#1B5E20',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    padding: 24,
    maxHeight: '80%',
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#EEE',
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '900',
    color: '#1B5E20',
    textAlign: 'center',
  },
  modalSub: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 24,
  },
  setsScroll: {
    marginBottom: 10,
  },
  setItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F9FBF9',
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#EDF2ED',
  },
  setItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  setItemInfo: {
    flex: 1,
  },
  setItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2E3D2F',
  },
  setItemDept: {
    fontSize: 12,
    color: '#6A8A6D',
    marginTop: 2,
  },
  cancelBtn: {
    height: 54,
    borderRadius: 16,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  cancelBtnT: {
    fontWeight: '800',
    color: '#777',
  },
  emptySets: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptySetsText: {
    fontSize: 14,
    color: '#999',
    marginTop: 12,
    marginBottom: 24,
    textAlign: 'center',
  },
  closeBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#1B5E20',
    borderRadius: 12,
  },
  closeBtnText: {
    color: 'white',
    fontWeight: '800',
  },
});
